import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'

import { shouldRetry, useMe } from '@/api/queries'
import { landingPath } from '@/lib/access'
import { AuthProvider } from '@/auth/AuthProvider'
import { RequireAuth } from '@/auth/RequireAuth'
import { AppShell } from '@/components/AppShell'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AccountPage } from '@/pages/AccountPage'
import { CallbackPage } from '@/pages/CallbackPage'
import { ChannelsPage } from '@/pages/ChannelsPage'
import { ConnectionsPage } from '@/pages/ConnectionsPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { InvoicesPage } from '@/pages/InvoicesPage'
import { LandingPage } from '@/pages/LandingPage'
import { ArchivePage } from '@/pages/ArchivePage'
import { BillingPage } from '@/pages/BillingPage'
import { MessagesPage } from '@/pages/MessagesPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { PlanPage } from '@/pages/PlanPage'
import { PolicePage } from '@/pages/PolicePage'
import { SecretsPage } from '@/pages/SecretsPage'
import { RegisterPage } from '@/pages/RegisterPage'
import { SetupPage } from '@/pages/SetupPage'
import { SkillsPage } from '@/pages/SkillsPage'
import { StaffFeedbackPage } from '@/pages/StaffFeedbackPage'
import { UploadsPage } from '@/pages/UploadsPage'
import { UsersPage } from '@/pages/UsersPage'
import { VerifyPage } from '@/pages/VerifyPage'
import { ZoneMembersPage } from '@/pages/ZoneMembersPage'
import { ZonesPage } from '@/pages/ZonesPage'
import { StaffAccountPage } from '@/staff/pages/StaffAccountPage'
import { StaffAccountsPage } from '@/staff/pages/StaffAccountsPage'
import { StaffAuditPage } from '@/staff/pages/StaffAuditPage'
import { StaffDashboardPage } from '@/staff/pages/StaffDashboardPage'
import { StaffDeletionsPage } from '@/staff/pages/StaffDeletionsPage'
import { StaffRefundsPage } from '@/staff/pages/StaffRefundsPage'
import { StaffLedgerPage } from '@/staff/pages/StaffLedgerPage'
import { StaffTeamPage } from '@/staff/pages/StaffTeamPage'
import { StaffUserPage } from '@/staff/pages/StaffUserPage'
import { StaffUsersPage } from '@/staff/pages/StaffUsersPage'
import { StaffZonePage } from '@/staff/pages/StaffZonePage'
import { ThemeProvider } from '@/theme/ThemeProvider'
import { useViewAs } from '@/staff/hooks'
import { ZoneProvider } from '@/zone/ZoneProvider'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      refetchOnWindowFocus: true,
      staleTime: 10_000,
    },
    mutations: {
      retry: false,
    },
  },
})

/** `/app`: a BILLING-role member has no overview (no zones), so they land on the plan. */
function HomeRoute() {
  const me = useMe()
  const landing = me.data === undefined ? null : landingPath({ role: me.data.role, billing: me.data.billing })
  if (landing !== null) return <Navigate to={landing} replace />
  return <DashboardPage />
}

/** A view-as session or break-glass grant starting or ending is a different console: remount the zone state. */
function ViewAsZoneProvider() {
  const session = useViewAs()
  const key = session === null ? 'own' : `${session.impersonationId}:${session.breakglass?.grantId ?? ''}`
  return (
    <ZoneProvider key={key}>
      <AppShell />
    </ZoneProvider>
  )
}

export function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider delayDuration={200}>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/verify" element={<VerifyPage />} />
                <Route path="/auth/callback" element={<CallbackPage />} />

                <Route
                  path="/app"
                  element={
                    <RequireAuth>
                      <ViewAsZoneProvider />
                    </RequireAuth>
                  }
                >
                  <Route index element={<HomeRoute />} />
                  <Route path="zones" element={<ZonesPage />} />
                  <Route path="zones/:zoneId/members" element={<ZoneMembersPage />} />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="account" element={<AccountPage />} />
                  <Route path="plan" element={<PlanPage />} />
                  <Route path="invoices" element={<InvoicesPage />} />
                  <Route path="billing" element={<BillingPage />} />
                  <Route path="messages" element={<MessagesPage />} />
                  <Route path="archive" element={<ArchivePage />} />
                  <Route path="connections" element={<ConnectionsPage />} />
                  <Route path="channels" element={<ChannelsPage />} />
                  <Route path="police" element={<PolicePage />} />
                  <Route path="secrets" element={<SecretsPage />} />
                  <Route path="uploads" element={<UploadsPage />} />
                  <Route path="setup" element={<SetupPage />} />
                  <Route path="skills" element={<SkillsPage />} />
                  <Route path="staff" element={<StaffDashboardPage />} />
                  <Route path="staff/accounts" element={<StaffAccountsPage />} />
                  <Route path="staff/accounts/:accountId" element={<StaffAccountPage />} />
                  <Route path="staff/users" element={<StaffUsersPage />} />
                  <Route path="staff/users/:userId" element={<StaffUserPage />} />
                  <Route path="staff/zones/:zoneId" element={<StaffZonePage />} />
                  <Route path="staff/ledger" element={<StaffLedgerPage />} />
                  <Route path="staff/refunds" element={<StaffRefundsPage />} />
                  <Route path="staff/deletions" element={<StaffDeletionsPage />} />
                  <Route path="staff/feedback" element={<StaffFeedbackPage />} />
                  <Route path="staff/team" element={<StaffTeamPage />} />
                  <Route path="staff/audit" element={<StaffAuditPage />} />
                  {/* Pre-zones path; connections replaced sessions in §16. */}
                  <Route path="sessions" element={<Navigate to="/app/connections" replace />} />
                </Route>

                <Route path="/dashboard" element={<Navigate to="/app" replace />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}
